import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  map,
  shareReplay,
  Subject,
  switchMap,
} from 'rxjs';
import { Customer } from 'server/shared/models/customer';
import {
  Employment,
  EmploymentStatusEnum,
} from 'server/shared/models/employment';
import { EmploymentInviteeStatusEnum } from 'server/shared/models/employmentInviteeStatusEnum';
import { PoolerUser } from 'server/shared/models/user';
import { rxFilterIsNotEmpty } from 'src/helpers/filterIsNotEmpty';
import { CustomerService } from '../../services/customer.service';
import { EmploymentService } from '../../services/employment.service';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-pooler-employment-list-item',
  templateUrl: './pooler-employment-list-item.component.html',
  styleUrls: ['./pooler-employment-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoolerEmploymentListItemComponent implements OnDestroy {
  destroyed$ = new Subject<void>();

  private employmentInput$ = new BehaviorSubject<Employment | undefined>(
    undefined,
  );
  employment$ = this.employmentInput$.pipe(rxFilterIsNotEmpty());

  private currentUserInput$ = new BehaviorSubject<PoolerUser | undefined>(
    undefined,
  );
  currentUser$ = this.currentUserInput$.pipe(rxFilterIsNotEmpty());

  private isSelfSignInInput$ = new BehaviorSubject<boolean | undefined>(
    undefined,
  );
  isSelfSignIn$ = this.isSelfSignInInput$.asObservable();

  // Derived observables
  selfInvitee$ = combineLatest([this.employment$, this.currentUser$]).pipe(
    map(([employment, currentUser]) =>
      employment.invitees.find((invitee) => invitee.userId === currentUser._id),
    ),
    shareReplay(1),
  );

  ownStatus$ = this.selfInvitee$.pipe(
    map(
      (selfInvitee) => selfInvitee?.status || EmploymentInviteeStatusEnum.OPEN,
    ),
  );

  eligibleCustomers$ = combineLatest([
    this.currentUser$,
    this.employment$,
  ]).pipe(
    switchMap(([currentUser, employment]) =>
      this.customerService.readyPoolerCustomers$.pipe(
        map((customers) =>
          customers.filter((customer) =>
            this.customerAllowsPoolerForEmployment(
              customer,
              currentUser,
              employment,
            ),
          ),
        ),
      ),
    ),
    shareReplay(1),
  );

  @Input() set employment(employment: Employment | undefined) {
    this.employmentInput$.next(employment);
  }
  get employment() {
    return this.employmentInput$.value;
  }

  @Input() set currentUser(currentUser: PoolerUser | undefined) {
    this.currentUserInput$.next(currentUser);
  }
  get currentUser() {
    return this.currentUserInput$.value;
  }

  @Input() set isSelfSignIn(isSelfSignIn: boolean | undefined) {
    this.isSelfSignInInput$.next(isSelfSignIn);
  }
  get isSelfSignIn() {
    return this.isSelfSignInInput$.value;
  }

  constructor(
    private employmentService: EmploymentService,
    private customerService: CustomerService,
    private userService: UserService,
  ) {}

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
  async acceptRequest() {
    const isSelfSignIn = this.isSelfSignInInput$.value;
    const employment = this.employmentInput$.value;

    if (isSelfSignIn) {
      if (!employment) {
        throw new Error('Missing employment for Employment self-sign-in');
      }
      const eligibleCustomers = await firstValueFrom(this.eligibleCustomers$);
      const customer = eligibleCustomers?.[0];
      if (!customer) {
        throw new Error('Missing customer for Employment self-sign-in');
      }
      this.employmentService
        .employmentSelfSignIn(
          employment._id,
          customer,
          EmploymentInviteeStatusEnum.AVAILABLE,
        )
        .then();
      return;
    }

    const selfInvitee = await firstValueFrom(this.selfInvitee$);
    if (!employment || !selfInvitee) {
      throw new Error('Missing data for Employment invitation answer');
    }
    await this.employmentService.answerEmploymentInvitation(
      employment._id,
      selfInvitee.id,
      EmploymentInviteeStatusEnum.AVAILABLE,
    );
  }

  async declineRequest() {
    const isSelfSignIn = this.isSelfSignInInput$.value;
    const employment = this.employmentInput$.value;

    if (isSelfSignIn) {
      if (!employment) {
        throw new Error('Missing employment for Employment self-sign-in');
      }
      const eligibleCustomers = await firstValueFrom(this.eligibleCustomers$);
      const customer = eligibleCustomers?.[0];
      if (!customer) {
        throw new Error('Missing customer for Employment self-sign-in');
      }
      this.employmentService
        .employmentSelfSignIn(
          employment._id,
          customer,
          EmploymentInviteeStatusEnum.UNAVAILABLE,
        )
        .then();
      return;
    }

    const selfInvitee = await firstValueFrom(this.selfInvitee$);
    if (!employment || !selfInvitee) {
      throw new Error('Missing data for Employment invitation answer');
    }
    await this.employmentService.answerEmploymentInvitation(
      employment._id,
      selfInvitee.id,
      EmploymentInviteeStatusEnum.UNAVAILABLE,
    );
  }

  customerAllowsPoolerForEmployment(
    customer: Customer,
    currentUser: PoolerUser,
    employment: Employment,
  ): boolean {
    const customerInformation = currentUser.customerInformation?.[customer._id];
    if (!customerInformation) {
      console.error(
        'missing customerInformation to evaluate customer for pooler',
      );
      return true;
    }
    const isAssignedToDepartment = customerInformation.departmentIds?.includes(
      employment.department.id,
    );
    const joinDate = customerInformation.joinDate;
    const leaveDate = customerInformation.leaveDate;
    const matchesJoinAndLeaveDate =
      (!joinDate || employment.startDate >= joinDate) &&
      (!leaveDate || employment.endDate <= leaveDate);

    return !!(isAssignedToDepartment && matchesJoinAndLeaveDate);
  }

  readonly EmploymentInviteeStatusEnum = EmploymentInviteeStatusEnum;
  readonly EmploymentStatusEnum = EmploymentStatusEnum;

  async hideJob() {
    const employment = this.employmentInput$.value;
    if (!employment) {
      throw new Error('Missing data for Employment invitation answer');
    }
    await this.employmentService.hideEmploymentForPooler(employment._id);
  }
}
